import {
  throwError as observableThrowError,
  of as observableOf,
  Observable,
  Subscription,
  ConnectableObservable,
} from 'rxjs';

import {map, first, catchError} from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  IterableDiffers,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SmdPaginationModel, SmdPaginatorComponent} from '../smd-paginator/paginator.component';
import {ResourceService, SearchTerm} from '../../../resource/resource.service';
import {SearchLimitation} from '../../../resource/filterresult';
import {LayoutService} from '../../../layout/layout.service';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {CacheService} from '../../../cache/cache.service';
import {DisplayAttribute} from '../../../resource/displayattribute';
import {DatePipe} from '@angular/common';
import {LanguageService} from '../../../language/language.service';
import {SmdDatatableHeader} from './datatable-header.component';
import {SmdDataTableColumn} from './datatable-column';
import {
  SmdDataRowIcon,
  SmdDataRowMark,
  SmdDataRowModel,
  SmdDataTableRowComponent,
} from './datatable-row.component';
import {AddViewEvent} from '../../../resource/events/addviewevent';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {DomUtilsService} from '../../../utils/dom-utils.service';

@Component({
  selector: 'smd-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.smd-responsive]': 'responsive',
  },
  standalone: false,
})
export class SmdDataTable implements AfterViewInit, OnDestroy {
  languageResource: Observable<string>;

  rows: SmdDataRowModel[] = [];
  private visibleRows: SmdDataRowModel[] = [];
  loadTimestamp: Date = null;
  loadingTime: number = null;

  @ViewChild(SmdPaginatorComponent, {static: true}) paginatorComponent: SmdPaginatorComponent;
  @ContentChild(SmdDatatableHeader, {static: true}) header: SmdDatatableHeader;
  columns: SmdDataTableColumn[] = [];

  @ViewChild('btnScrollLeft', {static: true}) btnScrollLeft: ElementRef;
  @ViewChild('btnScrollRight', {static: true}) btnScrollRight: ElementRef;
  @ViewChild('scrollPane', {static: true}) scrollPane: ElementRef;
  @ViewChildren('dataTableRow') rowComponents: QueryList<SmdDataTableRowComponent>;

  searchScopeConfigs: SearchScopeConfiguration[] = null;
  private differSearchScope: any;

  selectedSearchScope: SearchScopeConfiguration;

  filter: string = null;
  attributes: string;
  index: number = 0;
  models: any[] = [];
  @Input() checked: boolean = false;
  @Input() paginated: boolean = true;
  @Input() title: string = null;
  @Input() headerRowIcon: SmdDataRowIcon = null;
  @Input() headerRowIconTooltip: string = null;
  @Input() placeFilterInDatatableHeader: boolean = true;
  @Input() eachRowIcon: SmdDataRowIcon = null;
  @Input() disableLinks: boolean = false;
  private _remoteSpecialMarkedObjectIDs: string[] = null;
  @Input() remoteSpecialMark: SmdDataRowMark = null;
  @Input() remoteSpecialIcon: SmdDataRowIcon = null;
  @Input() localRowModels: SmdDataRowModel[] = null;
  @Input() localColumns: string[] = null;
  @Input() localResultObjectType: string = null;
  @Input() selectionMode: SmdDataSelectionMode = SmdDataSelectionMode.Multiple;
  @Input() additionalLoadedAttributes: string[] = null;
  @Input() showLoadingTime: boolean = false;
  @Input() emptyResultText: string = null;

  useLocal: boolean;
  @Input() mergeRemoteAndLocal: boolean = false;

  @Input()
  set defaultPageSize(strVal: string) {
    const ps: number = Number(strVal);

    if (ps == null || ps < 1) return;
    let ranges: number[] = [10, 25, 50, 100];
    if (ranges.indexOf(ps) === -1) {
      ranges.push(ps);
      ranges = ranges.sort((a, b) => a - b);
    }
    this.paginatorComponent.ranges = ranges;
    this.paginatorComponent.defaultRange = ps;
    this.paginatorComponent.selectedRange = ps;
  }

  @Input() responsive: boolean = false;

  @Input() set remoteSpecialMarkedObjectIDs(value: string[]) {
    this._remoteSpecialMarkedObjectIDs = value;
    this._updateTotalCount();
  }

  private sortColumn: string = null;
  private sortAscending: boolean = true;
  private resourceSubscription: Subscription = null;

  private totalCount: number = 0;
  displayedTotalCount: number = 0;
  loading: boolean = true;

  @Output() onRowSelected: EventEmitter<{model: any; checked: boolean}> = new EventEmitter<{
    model: any;
    checked: boolean;
  }>();
  @Output() onAllRowsSelected: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onRowIconClicked: EventEmitter<SmdDataRowModel | SmdDataRowModel[]> = new EventEmitter<
    SmdDataRowModel | SmdDataRowModel[]
  >();
  @Output() onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();
  @Output() onCloseView: EventEmitter<{}> = new EventEmitter<{}>();
  @Output() onRowsReloaded: EventEmitter<SmdDataRowModel[]> = new EventEmitter<SmdDataRowModel[]>();
  @Output() onError: EventEmitter<any> = new EventEmitter<any>();

  private _htmlElement: HTMLElement;

  constructor(
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private cacheService: CacheService,
    elementRef: ElementRef,
    private datePipe: DatePipe,
    private languageService: LanguageService,
    public changeDetector: ChangeDetectorRef,
    private domUtils: DomUtilsService,
    private envConfig: EnvironmentConfigService,
    private snackBar: MatSnackBar,
  ) {
    this.languageResource = this.cacheService.getLanguageResource();
    this._htmlElement = elementRef.nativeElement;
  }

  private observer: MutationObserver;

  observableCache: ConnectableObservable<any>[] = [];
  observableConnections: Subscription[] = [];

  ngOnDestroy(): void {
    this.observableConnections.forEach(s => s.unsubscribe());
  }

  ngAfterViewChecked(): void {
    //this.onResize();
  }

  ngAfterViewInit(): void {
    this.onResize();

    const scrollPane: HTMLElement = this.scrollPane.nativeElement;

    this.scrollPane.nativeElement.onscroll = () => this.refreshScrollButtons();

    this.btnScrollRight.nativeElement.addEventListener('click', () => {
      const scrollLeft: number = scrollPane.scrollLeft + Math.max(30, scrollPane.offsetWidth * 0.2);
      this.domUtils.smoothHorizontalScroll(scrollPane, scrollLeft, 300);
    });

    this.btnScrollLeft.nativeElement.addEventListener('click', () => {
      const scrollLeft: number = scrollPane.scrollLeft - Math.max(30, scrollPane.offsetWidth * 0.2);
      this.domUtils.smoothHorizontalScroll(scrollPane, scrollLeft, 300);
    });

    const self = this;
    this.observer = new MutationObserver(mutations => {
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'hidden' &&
          mutation.target['hidden'] == false
        ) {
          self.onResize();
        }
      });
    });
    this.observer.observe(this._htmlElement, {
      attributes: true,
      childList: true,
      characterData: true,
    });
  }

  @HostListener('window:resize', ['$event.target'])
  onResize(): void {
    if (this._htmlElement.hidden) return;

    this.header.changeDetector.markForCheck();
    this.changeDetector.markForCheck();

    const elem: Element = this._htmlElement.getElementsByClassName('smd-table-container')[0];
    if (elem.clientWidth < 485 && (this.columns == null || this.columns.length > 2)) {
      this._htmlElement.classList.add('smallView');
    } else {
      this._htmlElement.classList.remove('smallView');
    }

    if (elem.clientWidth < 575) {
      const footers = this._htmlElement.querySelectorAll('.smd-data-table-footer');
      footers.forEach(footer => footer.classList.add('small-footer'));
    } else {
      const footers = this._htmlElement.querySelectorAll('.smd-data-table-footer');
      footers.forEach(footer => footer.classList.remove('small-footer'));
    }

    this.refreshScrollButtons();
    setTimeout(() => this.refreshScrollButtons(), 100);
    setTimeout(() => this.refreshScrollButtons(), 500);
  }

  refreshScrollButtons(): void {
    if (this.scrollPane == null) return;
    const scrollPane: HTMLElement = this.scrollPane.nativeElement;
    const btnLeft = this.btnScrollLeft.nativeElement;
    const btnRight = this.btnScrollRight.nativeElement;

    if (scrollPane.scrollWidth > scrollPane.offsetWidth) {
      if (scrollPane.scrollLeft == 0) {
        btnLeft.style.display = 'none';
        btnRight.style.top = scrollPane.offsetTop + 'px';
        btnRight.style.height = scrollPane.offsetHeight + 'px';
        btnRight.style.display = '';
      } else if (scrollPane.scrollLeft + 2 >= scrollPane.scrollWidth - scrollPane.offsetWidth) {
        btnLeft.style.top = scrollPane.offsetTop + 'px';
        btnLeft.style.height = scrollPane.offsetHeight + 'px';
        btnLeft.style.display = '';
        btnRight.style.display = 'none';
      } else {
        btnLeft.style.top = scrollPane.offsetTop + 'px';
        btnLeft.style.height = scrollPane.offsetHeight + 'px';
        btnLeft.style.display = '';
        btnRight.style.top = scrollPane.offsetTop + 'px';
        btnRight.style.height = scrollPane.offsetHeight + 'px';
        btnRight.style.display = '';
      }

      if (scrollPane.scrollHeight > scrollPane.offsetHeight) {
        const scrollBarWidth: number = scrollPane.offsetWidth - scrollPane.clientWidth;
        btnRight.style.right = scrollBarWidth + 'px';
      } else {
        btnRight.style.right = '0';
      }
    } else {
      btnLeft.style.display = 'none';
      btnRight.style.display = 'none';
    }
  }

  refreshData(): void {
    this.loadTimestamp = null;
    if (this.useLocal) {
      this.refreshLocalData();
    } else {
      this.fetchResources();
    }
  }

  public reloadList(
    searchScopeConfigurations: SearchScopeConfiguration[],
    defaultSearchScope?: SearchScopeConfiguration,
  ): void {
    this.loading = true;
    this.searchScopeConfigs = searchScopeConfigurations;
    this.visibleRows.length = 0;
    this.header?.filterComponent?.resetCustomFilter();
    this.onResize();
    if (this.searchScopeConfigs != null && this.searchScopeConfigs.length > 0) {
      this.searchScopeConfigs = this.searchScopeConfigs.sort(
        (a: SearchScopeConfiguration, b: SearchScopeConfiguration) => {
          return a.order - b.order;
        },
      );
      if (defaultSearchScope != null) {
        this.selectedSearchScope = defaultSearchScope;
      } else {
        this.selectedSearchScope = this.searchScopeConfigs[0];
      }
    } else {
      this.selectedSearchScope = null;
    }
    this.refreshSearchScope();
    this.onResize();
  }

  refreshSearchScope() {
    this.paginatorComponent.currentPage = 1;
    this.index = 0;
    this.header.filterValue = '';
    this.sortColumn = null;
    this.checked = false;
    this.lastLimitationNotificationFilter = null;
    let scope: SearchScopeConfiguration = this.selectedSearchScope;
    if (scope != null) {
      this.sortColumn = scope.sortingattribute;
      this.sortAscending = scope.sortdirection !== 'desc';

      let displayColumns: string[];
      if (Array.isArray(scope.attributedisplayed)) {
        displayColumns = scope.attributedisplayed;
      } else if (scope.attributedisplayed != null) {
        displayColumns = scope.attributedisplayed.split(';');
      } else {
        displayColumns = [];
      }
      displayColumns = displayColumns.map(s => s.trim()).filter(s => s !== '');
      if (displayColumns.length === 0) {
        displayColumns.push(
          this.envConfig.getAttributeName('DisplayName'),
          this.envConfig.getAttributeName('Description'),
          this.envConfig.getAttributeName('ObjectType'),
        );
      }

      let attributes: string[] = displayColumns;
      if (this.additionalLoadedAttributes != null && this.additionalLoadedAttributes.length !== 0) {
        attributes = Array.from(new Set(attributes.concat(this.additionalLoadedAttributes)));
      }

      this.cacheService
        .getDisplayAttributes(attributes, scope.resultobjecttype)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe(
          (displayAttributes: DisplayAttribute[]) => {
            this.columns = [];
            displayAttributes
              .filter(da => {
                if (da != null && da.hasOwnProperty('Name'))
                  return displayColumns.indexOf(da.Name) !== -1;
                return false;
              })
              .forEach((attr, index) => {
                let col: SmdDataTableColumn = new SmdDataTableColumn(
                  this,
                  this.resourceService,
                  this.datePipe,
                  this.cacheService,
                  this.envConfig,
                );
                col.title = ResourceService.convertCamelCaseToNormal(attr.DisplayName);
                col.field = attr.Name;
                col.sortable = !attr.Multivalued;
                col.detailLink = index == 0 && !attr.Multivalued && attr.DataType !== 'Reference';
                col.dataType = attr.DataType;
                col.multiValued = attr.Multivalued;

                if (col.field === scope.sortingattribute) {
                  col.sortDir = scope.sortdirection;
                }

                if (attr.Description != null && attr.Description.trim().length > 0) {
                  col.titleTooltip = attr.Description.trim();
                }

                this.columns.push(col);
              });
            this.onResize();

            if (scope.xpath == null && !this.mergeRemoteAndLocal) {
              console.error('Filter is null, cannot display search result.');
              this.loading = false;
              this.onResize();
            } else {
              this.attributes = attributes.join(',');
              this.filter = scope.xpath;
              this.fetchResources(scope.xpath, this.attributes);
            }
          },
          () => {
            this.loading = false;
            this.onResize();
          },
        );
    } else {
      this.models.length = 0;
      this.rows = [];
    }
  }

  fetchResources(filter: string = this.filter, attributes: string = this.attributes) {
    if ((filter == null && !this.mergeRemoteAndLocal) || attributes == null) {
      this.loading = false;
      this._updateRows();
      this.onResize();
      return;
    }
    this.loadingTime = null;
    const startTime = Date.now();

    this.layoutService.updatePreloaderState('active');

    if (this.resourceSubscription != null) {
      if (!this.resourceSubscription.closed) {
        this.layoutService.updatePreloaderState('hide');
      }
      this.resourceSubscription.unsubscribe();
    }

    let pageSize: number = 0;
    if (this.paginatorComponent != null) {
      pageSize += this.paginatorComponent.selectedRange;
    }

    let sortColumn: string = this.sortColumn;
    let sortAsc: boolean = this.sortAscending;
    let index: number = this.index;
    let filteredLocalRMs: SmdDataRowModel[] = null;
    if (this.mergeRemoteAndLocal && this.localRowModels != null) {
      if (this.header.filterValue != null) {
        let userFilterValue: string = this.header.filterValue.trim();
        if (
          userFilterValue.length > 0 &&
          (this.selectedSearchScope.attributesearched ?? []).length > 0
        ) {
          // ';' separates independent search terms combined with OR; tokens within a term are
          // AND; a fully quoted term is matched exactly (mirrors the server-side search)
          const searchTerms: SearchTerm[] = ResourceService.parseSearchTerms(userFilterValue);
          const attributes: string[] = this.selectedSearchScope.attributesearched ?? [];
          filteredLocalRMs = this.localRowModels.filter((rm: SmdDataRowModel) => {
            if (rm == null || rm.model == null) return false;
            if (searchTerms.length === 0) return true;
            return searchTerms.some(term => {
              if (term.exact) {
                const exactValue = term.value.toLocaleLowerCase();
                return attributes.some(
                  attr => String(rm.model[attr]).toLocaleLowerCase() === exactValue,
                );
              }
              return ResourceService.splitSearchTerm(term.raw)
                .map(s => s.toLocaleLowerCase())
                .every(token =>
                  attributes.some(
                    attr => String(rm.model[attr]).toLocaleLowerCase().indexOf(token) !== -1,
                  ),
                );
            });
          });
        } else {
          filteredLocalRMs = this.localRowModels.slice();
        }
      } else {
        filteredLocalRMs = this.localRowModels.slice();
      }

      index -= filteredLocalRMs.length;
      if (index < 0) {
        index = 0;
        console.log('set index to null');
      }
      pageSize += filteredLocalRMs.length;

      if (
        this.remoteSpecialMark === SmdDataRowMark.HideRow &&
        this._remoteSpecialMarkedObjectIDs != null
      ) {
        pageSize += this._remoteSpecialMarkedObjectIDs.length;
      }

      if (sortColumn == null) {
        sortColumn = this.envConfig.getAttributeName('DisplayName');
        sortAsc = true;
      }
    }

    if (filter == null && this.mergeRemoteAndLocal) {
      this.models = [];
      if (filteredLocalRMs != null) this.totalCount = filteredLocalRMs.length;
      this.mergeModelsWithLocal(filteredLocalRMs);
      this._updateRows();
      this.layoutService.updatePreloaderState('hide');
      this.loading = false;
      this.onResize();
      return;
    }

    this.resourceSubscription = this.resourceService
      .searchAndFilterResources(
        filter,
        this.header.filterValue,
        this.selectedSearchScope.attributesearched ?? [],
        attributes,
        pageSize,
        index,
        sortColumn,
        sortAsc,
      )
      .pipe(
        map(res => {
          this.loadTimestamp = new Date();
          const endTime = Date.now();
          this.loadingTime = endTime - startTime;
          if (res == null) {
            this.totalCount = 0;
            this.displayedTotalCount = 0;
            this.paginatorComponent.numPages = 1;
            this.models = [];
          } else {
            this.totalCount = res.TotalCount;
            this.models = res.Results;
            if (this.mergeRemoteAndLocal) {
              if (filteredLocalRMs != null) this.totalCount += filteredLocalRMs.length;
              this.mergeModelsWithLocal(filteredLocalRMs);
            }
          }
          return res;
        }),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe(
        res => {
          this._updateRows(false);
          if (res === null || res.Finished === true) {
            this.layoutService.updatePreloaderState('hide');
            this.loading = false;
            if (res != null && res.Limitation != null) {
              this.notifySearchLimitation(res.Limitation);
            }
          }
          this.onResize();
        },
        () => {
          this.totalCount = 0;
          this.displayedTotalCount = 0;
          this.paginatorComponent.numPages = 1;
          this.models = [];
          this._updateRows();
          this.layoutService.updatePreloaderState('hide');
          this.loading = false;
          this.onResize();
        },
      );
  }

  mergeModelsWithLocal(filteredLocalRMs: SmdDataRowModel[]): void {
    if (filteredLocalRMs == null || filteredLocalRMs.length === 0) {
      this.createRemoteRowModels();
      return;
    }
    this.rows = [];
    const sortCol: string = this.sortColumn
      ? this.sortColumn
      : this.envConfig.getAttributeName('DisplayName');
    filteredLocalRMs.sort((a, b) => this.compareFunction(a.model, b.model, sortCol));
    if (this.models == null || this.models.length === 0) {
      if (filteredLocalRMs.length === 0) {
        return;
      }
      this.rows = filteredLocalRMs.slice(this.index - this.totalCount + filteredLocalRMs.length);
    } else if (
      this.compareFunction(
        filteredLocalRMs[filteredLocalRMs.length - 1].model,
        this.models[0],
        sortCol,
      ) < 0
    ) {
      // all locals are before the remotes
      let remoteStartIndex: number = this.index - filteredLocalRMs.length;
      if (remoteStartIndex < 0) remoteStartIndex = 0;
      const localStartIndex: number = this.index - remoteStartIndex;

      this.createRemoteRowModels();
      this.rows = filteredLocalRMs
        .slice(localStartIndex, localStartIndex + this.paginatorComponent.selectedRange)
        .concat(this.rows)
        .slice(0, this.paginatorComponent.selectedRange);
    } else if (
      this.compareFunction(
        filteredLocalRMs[0].model,
        this.models[this.models.length - 1],
        sortCol,
      ) > 0
    ) {
      // all locals after remotes
      const modelLengthBefore: number = this.models.length;

      if (this.index !== 0) {
        this.models = this.models.slice(filteredLocalRMs.length);
      }
      this.createRemoteRowModels();

      if (this.models.length === 0) {
        let localStartIndex: number;
        if (this.index < filteredLocalRMs.length - 1) {
          let requestStartIndex: number = this.index - filteredLocalRMs.length;
          if (requestStartIndex < 0) requestStartIndex = 0;
          localStartIndex = this.index - modelLengthBefore + requestStartIndex;
        } else {
          localStartIndex = filteredLocalRMs.length - modelLengthBefore;
        }
        this.rows = filteredLocalRMs.slice(
          localStartIndex,
          localStartIndex + this.paginatorComponent.selectedRange,
        );
      } else if (this.models.length < this.paginatorComponent.selectedRange) {
        this.rows = this.rows.concat(
          filteredLocalRMs.slice(0, this.paginatorComponent.selectedRange - this.models.length),
        );
      }
    } else {
      let localIndex: number = 0;
      let requestIndex: number = 0;

      if (this.index !== 0) {
        let localsBefore: number = 0;

        let remoteStartIndex: number = this.index - filteredLocalRMs.length;
        if (remoteStartIndex < 0) remoteStartIndex = 0;
        while (remoteStartIndex + requestIndex + localsBefore <= this.index) {
          if (requestIndex >= this.models.length) {
            requestIndex++;
            break;
          }
          while (
            this.compareFunction(
              filteredLocalRMs[localsBefore].model,
              this.models[requestIndex],
              sortCol,
            ) < 0
          ) {
            localsBefore++;
            if (localsBefore >= filteredLocalRMs.length) {
              break;
            }
          }
          requestIndex++;
        }
        requestIndex--;
        // remoteIndex = requestIndex + remoteStartIndex;
        // let totalIndex: number = (remoteIndex + localsBefore);
        // localIndex = localsBefore - (totalIndex - this.index);
        localIndex = this.index - requestIndex - remoteStartIndex;
      }
      for (let i = requestIndex; i < this.models.length; i++) {
        if (this.rows.length > this.paginatorComponent.selectedRange) break;
        if (localIndex >= filteredLocalRMs.length) {
          while (
            this.rows.length < this.paginatorComponent.selectedRange &&
            i < this.models.length
          ) {
            this.createRemoteRowModel(this.models[i]);
            i++;
          }
          break;
        }
        let compare = this.compareFunction(
          this.models[i],
          filteredLocalRMs[localIndex].model,
          sortCol,
        );
        if (compare === 0) {
          this.createRemoteRowModel(this.models[i]);
          this.rows.push(filteredLocalRMs[localIndex]);
          localIndex++;
        } else if (compare < 0) {
          this.createRemoteRowModel(this.models[i]);
        } else {
          while (compare > 0) {
            this.rows.push(filteredLocalRMs[localIndex]);
            localIndex++;
            if (localIndex >= filteredLocalRMs.length) break;
            compare = this.compareFunction(
              this.models[i],
              filteredLocalRMs[localIndex].model,
              sortCol,
            );
          }
          this.createRemoteRowModel(this.models[i]);
        }
      }
      if (this.rows.length < this.paginatorComponent.selectedRange) {
        this.rows = this.rows.concat(
          filteredLocalRMs.slice(
            localIndex,
            localIndex + this.paginatorComponent.selectedRange - this.rows.length,
          ),
        );
      }
      this.rows = this.rows.slice(0, this.paginatorComponent.selectedRange);
    }
  }

  private createRemoteRowModels(): void {
    this.rows = [];
    this.models.forEach((model: any, index: number) => {
      this.createRemoteRowModel(model, index);
    });
  }

  private createRemoteRowModel(model: any, index?: number) {
    if (model == null) {
      throw 'DataTable: Model is null in createRemoteRowModel';
    }
    let mark: SmdDataRowMark = null;
    let icon: SmdDataRowIcon = this.eachRowIcon;
    if (
      this._remoteSpecialMarkedObjectIDs != null &&
      model[this.envConfig.getAttributeName('ObjectID')] != null &&
      this._remoteSpecialMarkedObjectIDs.indexOf(
        model[this.envConfig.getAttributeName('ObjectID')],
      ) !== -1
    ) {
      mark = this.remoteSpecialMark;
      icon = this.remoteSpecialIcon;
    }
    if (index != null) {
      this.rows[index] = new SmdDataRowModel(model, this.checked, mark, icon);
    } else {
      this.rows.push(new SmdDataRowModel(model, this.checked, mark, icon));
    }
  }

  compareFunction(a: any, b: any, sortColumn: string): number {
    let compare = 0;
    const aVal: any = a[sortColumn];
    const bVal: any = b[sortColumn];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      compare = aVal.localeCompare(bVal);
    } else if (aVal == null && bVal != null) {
      compare = -1;
    } else if (aVal != null && bVal == null) {
      compare = 1;
    } else if (aVal == null && bVal == null) {
      compare = 0;
    } else {
      compare = aVal - bVal;
    }
    if (compare === 0) {
      if (
        [
          this.envConfig.getAttributeName('DisplayName'),
          this.envConfig.getAttributeName('ObjectID'),
        ].indexOf(sortColumn) === -1
      ) {
        return this.compareFunction(a, b, this.envConfig.getAttributeName('DisplayName'));
      } else if (this.sortColumn !== this.envConfig.getAttributeName('ObjectID')) {
        return this.compareFunction(a, b, this.envConfig.getAttributeName('ObjectID'));
      } else {
        return 0;
      }
    } else {
      return compare;
    }
  }

  refreshLocalData(): void {
    if (this.localRowModels == null) return;
    this.rows = this.localRowModels;
    this.totalCount = this.localRowModels.length;
    this.paginatorComponent.numPages = Math.ceil(
      this.totalCount / this.paginatorComponent.selectedRange,
    );

    if (this.localResultObjectType != null) {
      this.cacheService
        .getDisplayAttributes(this.localColumns, this.localResultObjectType)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe(
          (displayAttributes: DisplayAttribute[]) => {
            this.columns = [];
            let attributes: string = null;
            displayAttributes.forEach(attr => {
              if (attributes == null) {
                attributes = attr.Name;
              } else {
                attributes += ',' + attr.Name;
              }

              let col: SmdDataTableColumn = new SmdDataTableColumn(
                this,
                this.resourceService,
                this.datePipe,
                this.cacheService,
                this.envConfig,
              );
              col.title = ResourceService.convertCamelCaseToNormal(attr.DisplayName);
              col.field = attr.Name;
              col.sortable = false;
              col.dataType = attr.DataType;
              col.multiValued = attr.Multivalued;

              if (attr.Description != null && attr.Description.trim().length > 0) {
                col.titleTooltip = attr.Description.trim();
              }

              this.columns.push(col);
            });
            this.onResize();
          },
          () => {
            this.columns = [];
            this.onResize();
          },
        );
    }

    this._updateRows();
    this.loading = false;
    this.onResize();
  }

  _updateRows(pageChange: boolean = true) {
    if (!this.useLocal && !this.mergeRemoteAndLocal && this.models) {
      this.createRemoteRowModels();
    }
    this._updateVisibleRows(pageChange);
    this._updateTotalCount();
  }

  selectedRows(): SmdDataRowModel[] {
    return this.rows.filter(row => row.checked);
  }

  selectedRowsLength(): number {
    return this.selectedRows().length;
  }

  selectedModels(): any[] {
    return this.selectedRows().map(row => row.model);
  }

  _onMasterCheckChange() {
    this.rows.forEach((row: SmdDataRowModel) => {
      if (row.checked != this.checked) {
        row.checked = this.checked;
      }
    });
    this.onAllRowsSelected.emit(this.checked);
    this.markAllRowsForCheck();
    this.onResize();
  }

  markAllRowsForCheck(): void {
    this.onResize();
    this.rowComponents.forEach((rc: SmdDataTableRowComponent) => rc.changeDetector.markForCheck());
  }

  _onRowCheckChange(row: SmdDataRowModel) {
    let isMasterChecked = this.checked;
    if (row.checked) {
      if (this.selectionMode === SmdDataSelectionMode.Single) {
        this.rows.forEach((rm: SmdDataRowModel) => {
          if (rm.checked && rm != row) {
            rm.checked = false;
          }
        });
        this.markAllRowsForCheck();
        this.onResize();
      } else if (this.rows.filter(row => row.checked).length == this.rows.length) {
        this.checked = true;
      }
    } else {
      if (this.checked) {
        this.checked = false;
      }
    }
    this.onRowSelected.emit({
      model: row.model,
      checked: row.checked,
    });

    if (this.checked != isMasterChecked) {
      this.onAllRowsSelected.emit(this.checked);
    }
    this.header.changeDetector.markForCheck();
  }

  _onRowIconClick(row: SmdDataRowModel) {
    if (row == null) {
      this.onRowIconClicked.emit(this.visibleRows);
    } else {
      this.onRowIconClicked.emit(row);
    }
  }

  _onFilter(): void {
    this.index = 0;
    this.paginatorComponent.currentPage = 1;
    this.refreshData();
  }

  _sortColumn(column: SmdDataTableColumn) {
    if (column.sortable) {
      this.sortColumn = column.field;

      if (!column.sortDir) {
        column.sortDir = 'asc';
      } else {
        column.sortDir = column.sortDir == 'asc' ? 'desc' : null;
      }

      this.sortAscending = column.sortDir == 'asc';

      if (column.sortDir == null) {
        this.sortColumn = null;
      }

      this.columns.forEach((col: SmdDataTableColumn) => {
        if (col !== column) {
          col.sortDir = null;
        }
      });

      this.refreshData();
    }
  }

  _onPageChange(paginationModel: SmdPaginationModel) {
    if (paginationModel == null) return;
    this.index = (paginationModel.page - 1) * paginationModel.size;
    if (this.index < 0) {
      this.index = 0;
      this.paginatorComponent.currentPage = 1;
    } else if (this.index >= this.displayedTotalCount && this.displayedTotalCount > 0) {
      this.paginatorComponent.numPages = Math.ceil(
        this.displayedTotalCount / this.paginatorComponent.selectedRange,
      );
      this.paginatorComponent.currentPage = this.paginatorComponent.numPages;
      this.index = (this.paginatorComponent.currentPage - 1) * paginationModel.size;
    }
    this.checked = false;
    this.refreshData();
    this.refreshScrollButtons();
  }

  get ShowLocal(): boolean {
    return this.useLocal;
  }

  @Input()
  set ShowLocal(local: boolean) {
    this.useLocal = local;
    this.index = 0;
    this.loading = true;
    this.visibleRows = null;

    if (local) {
      this.refreshLocalData();
    } else {
      this.refreshSearchScope();
    }
  }

  set MergeRemoteAndLocal(merge: boolean) {
    this.mergeRemoteAndLocal = merge;
    this.refreshData();
  }

  private _updateVisibleRows(pageChange: boolean = true) {
    if (this.useLocal) {
      if (this.localRowModels == null) {
        this.visibleRows = [];
      } else {
        this.visibleRows = this.localRowModels
          .filter(x => x.mark !== SmdDataRowMark.HideRow)
          .slice(this.index, this.index + this.paginatorComponent.selectedRange);
      }
    } else {
      this.visibleRows = this.rows.filter(x => x.mark !== SmdDataRowMark.HideRow);
    }
    if (this.index !== 0 && this.visibleRows.length === 0 && pageChange) {
      this._onPageChange(
        new SmdPaginationModel(
          this.paginatorComponent.currentPage,
          this.paginatorComponent.selectedRange,
        ),
      );
      return;
    }
    this.onRowsReloaded.emit(this.visibleRows);
    this.refreshScrollButtons();
    setTimeout(this.refreshScrollButtons, 100);
    setTimeout(this.refreshScrollButtons, 500);
    setTimeout(this.refreshScrollButtons, 800);
    setTimeout(this.refreshScrollButtons, 1000);
  }

  private _updateTotalCount() {
    if (Array.isArray(this._remoteSpecialMarkedObjectIDs)) {
      this.displayedTotalCount = this.totalCount - this._remoteSpecialMarkedObjectIDs.length;
    } else {
      this.displayedTotalCount = this.totalCount;
    }

    if (this.paginatorComponent != null) {
      this.paginatorComponent.numPages = Math.ceil(
        this.displayedTotalCount / this.paginatorComponent.selectedRange,
      );
    }
  }

  _shouldRenderCheckbox() {
    return (
      this.rows.length > 0 &&
      this.header.shouldRenderCheckbox() &&
      this.selectionMode !== SmdDataSelectionMode.Disable
    );
  }

  formatNumber(number: Number, options?: Intl.NumberFormatOptions): Observable<string> {
    return this.languageService
      .getLanguage(this.cacheService.getSupportedLocaleConfiguration())
      .pipe(
        map((language: string) => {
          return number.toLocaleString(language, options);
        }),
      );
  }

  get isSelectionModeMultiple(): boolean {
    return this.selectionMode === SmdDataSelectionMode.Multiple;
  }

  get isSelectionModeSingle(): boolean {
    return this.selectionMode === SmdDataSelectionMode.Single;
  }

  getCaughtObservable(obs: Observable<any>): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(catchError((error, caught) => this.handleAsyncError(error, caught)));
  }

  handleAsyncError(error: any, caught?: Observable<any>) {
    if (this.onError != null) {
      this.onError.emit(error);
    }
    return observableOf(null);
  }

  handleError(error: any, caught?: Observable<any>) {
    if (this.onError != null) {
      this.onError.emit(error);
    }
    return observableThrowError(error);
  }

  private lastLimitationNotificationFilter: string = null;

  // informs the user once per search text that the search had to be limited (phases, attributes
  // or terms) to keep every request below the maximum query string length
  private notifySearchLimitation(limitation: SearchLimitation): void {
    const filterValue = this.header != null ? this.header.filterValue : null;
    if (filterValue === this.lastLimitationNotificationFilter) return;
    this.lastLimitationNotificationFilter = filterValue;
    this.languageResource.pipe(first()).subscribe(lr => {
      const message = ResourceService.searchLimitationMessage(limitation, lr);
      if (message != null) {
        this.snackBar.open(message, null, {duration: 8000});
      }
    });
  }
}

export enum SmdDataSelectionMode {
  Disable,
  Single,
  Multiple,
}
